import type { ApiResponse } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import type {
  AgentResponse,
  CreateAgentRequest,
  IntegrationToolResponse,
  McpToolResponse,
  NativeToolResponse,
  StoredTool,
  UpdateAgentRequest,
} from "../../../pavilion/schemas";
import { api } from "../api/api";

export interface AgentTools {
  native: NativeToolResponse[];
  mcp: McpToolResponse[];
  integrations?: IntegrationToolResponse[];
}

const AGENTS_KEY = "PAVILION_AGENTS";

export const listPavilionAgentsQueryOptions = () => ({
  queryKey: [AGENTS_KEY],
  queryFn: () => api.listAgents(),
});

export const getPavilionAgentQueryOptions = (agentName: string) => ({
  queryKey: [AGENTS_KEY, agentName],
  queryFn: () => api.getAgent(agentName),
  enabled: !!agentName,
  staleTime: 5 * 60 * 1000, // 5 minutes
  gcTime: 10 * 60 * 1000, // 10 minutes
});

export const listPavilionAgentToolsQueryOptions = (agentName: string) => ({
  queryKey: [AGENTS_KEY, agentName, "tools"],
  queryFn: () => api.listAgentTools(agentName),
  enabled: !!agentName,
});

export const listPavilionAgentMcpServersQueryOptions = (agentName: string) => ({
  queryKey: [AGENTS_KEY, agentName, "mcp-servers"],
  queryFn: async () => {
    const response = await api.getMcpServers(agentName);
    return response.data;
  },
  enabled: !!agentName,
});

export const listPavilionAgentConversationsQueryOptions = (
  agentName: string,
) => ({
  queryKey: [AGENTS_KEY, agentName, "conversations"],
  queryFn: () => api.listAgentConversations(agentName),
  enabled: !!agentName,
});

export function useCreatePavilionAgent() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (data: CreateAgentRequest) => api.createAgent(data),
    onSuccess: async (response: ApiResponse<AgentResponse>) => {
      // Prefetch the agent data before navigation
      await queryClient.prefetchQuery(
        getPavilionAgentQueryOptions(response.data.name),
      );

      // Navigate to the new agent
      router.navigate({
        to: "/pavilion/$agentName",
        params: { agentName: response.data.name },
        search: { leftTab: "system", rightTab: "console" },
      });

      // Invalidate the agents list in the background
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });
    },
  });
}

export function useUpdatePavilionAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentName,
      data,
    }: {
      agentName: string;
      data: UpdateAgentRequest;
    }) => api.updateAgent(agentName, data),
    onSuccess: (response, variables) => {
      // Update the specific agent query
      queryClient.setQueryData(
        [AGENTS_KEY, variables.agentName],
        (oldData: ApiResponse<AgentResponse> | undefined) => {
          if (oldData) {
            return {
              ...oldData,
              data: response.data,
            };
          }
          return oldData;
        },
      );

      // Invalidate the agents list to refresh the sidebar
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });
    },
  });
}

export function useDeletePavilionAgent() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: (agentName: string) => {
      return api.deleteAgent(agentName);
    },
    onSuccess: (_data, agentName) => {
      // Remove the agent from the cache
      queryClient.removeQueries({ queryKey: [AGENTS_KEY, agentName] });

      // Invalidate the agents list
      queryClient.invalidateQueries({ queryKey: [AGENTS_KEY] });

      // Navigate back to the pavilion home
      router.navigate({ to: "/pavilion" });
    },
    onError: (error, agentName) => {
      console.error(
        "❌ Delete mutation failed for agent:",
        agentName,
        "Error:",
        error,
      );
    },
  });
}

export function useUpdatePavilionAgentTools() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentName,
      tools,
    }: {
      agentName: string;
      tools: Array<Pick<StoredTool, "name" | "namespace" | "enabled">>;
    }) => api.updateAgentTools(agentName, tools),
    onSuccess: (_data, variables) => {
      // Invalidate the tools query to refetch
      queryClient.invalidateQueries({
        queryKey: [AGENTS_KEY, variables.agentName, "tools"],
      });
    },
  });
}

export function useAddMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentName,
      url,
      name,
    }: {
      agentName: string;
      url: string;
      name: string;
    }) => api.addMcpServer(agentName, { url, name }),
    onSuccess: async (_data, variables) => {
      // Force refetch of MCP servers data
      await queryClient.refetchQueries({
        queryKey: [AGENTS_KEY, variables.agentName, "mcp-servers"],
      });
      // Also invalidate tools in case new tools are available
      queryClient.invalidateQueries({
        queryKey: [AGENTS_KEY, variables.agentName, "tools"],
      });
    },
  });
}

export function useRemoveMcpServer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentName,
      serverId,
    }: {
      agentName: string;
      serverId: string;
    }) => api.removeMcpServer(agentName, serverId),
    onSuccess: async (_data, variables) => {
      // Force refetch of MCP servers data
      await queryClient.refetchQueries({
        queryKey: [AGENTS_KEY, variables.agentName, "mcp-servers"],
      });
      // Also invalidate tools since tools may be removed
      queryClient.invalidateQueries({
        queryKey: [AGENTS_KEY, variables.agentName, "tools"],
      });
    },
  });
}
